import { apiClient } from '@/shared/api';
import type { Brand, BrandAttribute, BrandProperty, BrandAgentLink } from '../types';

const BASE_PATH = '/v1/admin/organisations';

// Brands
export async function getOrganisationBrands(orgId: string): Promise<Brand[]> {
  const response = await apiClient.get<{ data?: { brands: Brand[] }; brands?: Brand[] }>(`${BASE_PATH}/${orgId}/brands`);
  return response.data.data?.brands || response.data.brands || [];
}

export async function addOrganisationBrand(orgId: string, data: Partial<Brand>): Promise<Brand> {
  const response = await apiClient.post<Brand>(`${BASE_PATH}/${orgId}/brands`, data);
  return response.data;
}

export async function updateOrganisationBrand(
  orgId: string,
  brandId: string,
  data: Partial<Brand>
): Promise<Brand> {
  const response = await apiClient.post<Brand>(`${BASE_PATH}/${orgId}/brands/${brandId}`, data);
  return response.data;
}

export async function removeOrganisationBrand(orgId: string, brandId: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${orgId}/brands/${brandId}`);
}

// Brand Attributes
export async function getBrandAttributes(
  orgId: string,
  brandId: string
): Promise<BrandAttribute[]> {
  const response = await apiClient.get<{ data?: { static_attribute_definitions: BrandAttribute[] }; static_attribute_definitions?: BrandAttribute[] }>(
    `${BASE_PATH}/${orgId}/brands/${brandId}/attributes/static`
  );
  return response.data.data?.static_attribute_definitions || response.data.static_attribute_definitions || [];
}

// Brand Properties
export async function getBrandProperties(
  orgId: string,
  brandId: string
): Promise<BrandProperty[]> {
  const response = await apiClient.get<{ data?: { properties: BrandProperty[] }; properties?: BrandProperty[] }>(
    `${BASE_PATH}/${orgId}/brands/${brandId}/properties`
  );
  return response.data.data?.properties || response.data.properties || [];
}

export async function addBrandProperty(
  orgId: string,
  brandId: string,
  property: string
): Promise<BrandProperty> {
  const response = await apiClient.post<BrandProperty>(
    `${BASE_PATH}/${orgId}/brands/${brandId}/properties`,
    { property }
  );
  return response.data;
}

export async function removeBrandProperty(
  orgId: string,
  brandId: string,
  propertyId: string
): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${orgId}/brands/${brandId}/properties/${propertyId}`);
}

// Brand Agent Links
export async function getBrandAgentLinks(
  orgId: string,
  brandId: string
): Promise<BrandAgentLink[]> {
  const response = await apiClient.get<{ data?: { brandAgentLinks: BrandAgentLink[] }; brandAgentLinks?: BrandAgentLink[] }>(
    `${BASE_PATH}/${orgId}/brand/${brandId}/agents`
  );
  return response.data.data?.brandAgentLinks || response.data.brandAgentLinks || [];
}

export async function linkBrandToAgent(
  orgId: string,
  data: {
    brandId: string;
    brandName: string;
    agentOrganisationId: string;
    agentOrganisationName: string;
  }
): Promise<void> {
  await apiClient.post(`${BASE_PATH}/${orgId}/brand/link-agent`, data);
}

export async function unlinkBrandFromAgent(
  orgId: string,
  agentLinkId: string
): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${orgId}/brand/link-agent/${agentLinkId}`);
}
